import React, { useMemo, useState } from 'react';
import { ChevronRightIcon, PackageIcon, RotateCcwIcon } from 'lucide-react';
import { ReturnDetailsModal } from './ReturnDetailsModal';
import { type ReturnRequestRecord, useStore } from '../../lib/store';
type ReturnRecord = ReturnRequestRecord | {
  id: string;
  originalOrderId: string;
  returnDate: string;
  status: 'approved' | 'pending' | 'rejected';
  reason: string;
  refundMethod: string;
  refundAmount: number;
  items: Array<{
    name: string;
    quantity: number;
    price: number;
    image: string;
  }>;
  trackingNumber?: string;
  estimatedRefundDate?: string;
};
const historicalReturns: ReturnRecord[] = [{
  id: 'RET-00012',
  originalOrderId: 'ORD-13951',
  returnDate: '2025-01-16',
  status: 'approved',
  reason: 'Product damaged',
  refundMethod: 'Original payment method',
  refundAmount: 12.99,
  items: [{
    name: 'Panadol Extra Tablets 24s',
    quantity: 1,
    price: 12.99,
    image: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400&h=400&fit=crop'
  }],
  trackingNumber: 'TRK-2024-001234',
  estimatedRefundDate: '2025-01-20'
}, {
  id: 'RET-00011',
  originalOrderId: 'ORD-13948',
  returnDate: '2025-01-12',
  status: 'pending',
  reason: 'Wrong item received',
  refundMethod: 'Store credit',
  refundAmount: 24.99,
  items: [{
    name: 'Vitamin D3 Supplement',
    quantity: 1,
    price: 24.99,
    image: 'https://images.unsplash.com/photo-1550572017-4bfa2d5c8f1d?w=400&h=400&fit=crop'
  }],
  estimatedRefundDate: '2025-01-18'
}];
const statusClass: Record<ReturnRecord['status'], string> = {
  approved: 'bg-emerald-50 text-emerald-700',
  pending: 'bg-amber-50 text-amber-800',
  rejected: 'bg-rose-50 text-rose-700'
};
export function ReturnsSection() {
  const [selectedReturn, setSelectedReturn] = useState<ReturnRecord | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const returnRequests = useStore(state => state.returnRequests);
  const returns = useMemo(() => [...returnRequests, ...historicalReturns], [returnRequests]);
  const openDetails = (returnItem: ReturnRecord) => {
    setSelectedReturn(returnItem);
    setIsModalOpen(true);
  };
  return <>
    <div className="mx-auto max-w-2xl space-y-4" data-id="element-1083" data-mp="3831r2673e8tm">
      <div data-id="element-1084" data-mp="337mcfc19g8w5"><h2 className="text-xl font-bold text-gray-900" data-id="element-1085" data-mp="337mah6hp9yti">Returns & refunds</h2><p className="mt-1 text-sm text-gray-500" data-id="element-1086" data-mp="3hvpkqs2rzwo4">Track return requests and refund progress.</p></div>
      {returns.length === 0 ? <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-8 text-center" data-id="element-1087" data-mp="3d3pd0dggrzgz"><PackageIcon className="mx-auto h-8 w-8 text-gray-300" data-id="element-1088" data-mp="3quugxrqv7dx4" /><h3 className="mt-3 text-base font-bold text-gray-900" data-id="element-1089" data-mp="3qxqcj9f8pqie">No returns yet</h3><p className="mt-1 text-sm text-gray-500" data-id="element-1090" data-mp="3mu0u71p5s55t">Return eligible items from your order details.</p></div> : <div className="space-y-3" data-id="element-1091" data-mp="4nw86vvhwcfr">{returns.map(returnItem => <button key={returnItem.id} type="button" onClick={() => openDetails(returnItem)} className="flex w-full items-center gap-3 rounded-2xl border border-gray-200 bg-white p-4 text-left shadow-[0_2px_10px_rgba(15,23,42,0.04)]" data-id="element-1092" data-mp="3vugrtuj80i2r"><div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-50" data-id="element-1093" data-mp="21i94p3d555hj"><RotateCcwIcon className="h-5 w-5 text-[#16726c]" data-id="element-1094" data-mp="gzybehxxlz24" /></div><span className="min-w-0 flex-1" data-id="element-1095" data-mp="ly03tth8zaq9"><span className="flex items-center justify-between gap-2" data-id="element-1096" data-mp="ly9lmts52bkv"><span className="text-sm font-bold text-gray-900" data-id="element-1097" data-mp="lzue8duw7kg3">{returnItem.id}</span><span className="text-sm font-bold text-[#16726c]" data-id="element-1098" data-mp="78kf0j1a1fd7">SAR {returnItem.refundAmount.toFixed(2)}</span></span><span className="mt-1 block truncate text-xs text-gray-500" data-id="element-1099" data-mp="c6dmdfebgff2">Order {returnItem.originalOrderId} · {returnItem.items.length} {returnItem.items.length === 1 ? 'item' : 'items'}</span><span className={`mt-2 inline-flex rounded-full px-2 py-1 text-[10px] font-bold ${statusClass[returnItem.status]}`} data-id="element-1100" data-mp="38a0ghzf5gmq">{returnItem.status === 'pending' ? 'Refund requested' : returnItem.status}</span></span><ChevronRightIcon className="h-5 w-5 shrink-0 text-gray-400" data-id="element-1101" data-mp="3pfi0r5vhexbw" /></button>)}</div>}
    </div>
    {selectedReturn && <ReturnDetailsModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} returnData={selectedReturn} data-id="element-1102" data-mp="1ip21prf2o3mn" />}
  </>;
}